import React, { useState } from 'react';
import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import Column from '../../../common/layout/Column';
import { ClaimContext } from '../types';
import { ClaimStepContainerProps } from '../common/ClaimStepContainer';
import RichLocale from '../../../common2/RichLocale';
import Button from '../../../common2/Button';
import { useMutation } from '@tanstack/react-query';
import container from '../../../../container';
import { Select } from '../../../common2/Select/Select';
import LinkPreview from '../../../common/LinkPreview';
import Locale from '../../../Translate';
import Text from '../../../common/Text/Text';
import Heading3 from '../../../common/Heading3';
import { languageLabels } from '../claimsHelpers';
import styles from './DORSigningLinkStep.scss';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';

type ClaimContextForDORSigningLinkStep = Pick<ClaimContext, 'shipmentId' | 'recipientEmail' | 'availableDORLanguages'>;

export type DORSigningLinkStepProps = {
  claim: ClaimContextForDORSigningLinkStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  shopId: number;
} & ClaimStepContainerProps;

export const mapAvailableLanguages = (availableDORLanguages: string[]) =>
  availableDORLanguages
    .map((language) => ({
      value: language,
      label: languageLabels[language] || language.toUpperCase(),
    }))
    .sort((a, b) => a.value.localeCompare(b.value));

function DORSigningLinkStep(props: DORSigningLinkStepProps) {
  const {
    claim,
    as: Container,
    isDORRequired,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
    shopId,
    ...restProps
  } = props;

  const { shipmentId, recipientEmail, availableDORLanguages } = claim;

  if (!shipmentId || !recipientEmail || !availableDORLanguages) {
    console.error({ shipmentId, recipientEmail, availableDORLanguages });
    throw new Error('The necessary data for the DOR step is not available');
  }

  const [language, setLanguage] = useState('');

  const mutation = useMutation({
    mutationKey: ['createDorSigningLink', shopId, shipmentId, language],
    mutationFn: (language: string) =>
      container.touchpointsApi.createDorSigningLink(shopId, shipmentId, language, recipientEmail),
  });

  function onClick() {
    mutation.mutate(language);
  }

  const options = mapAvailableLanguages(availableDORLanguages);

  return (
    <Container {...restProps} isDisabled={mutation.isLoading}>
      <SidePanelContentSection heading="E_SIGNATURE_DOR_SECTION_HEADER" />
      <RichLocale ink>E_SIGNATURE_DOR_SECTION_INFO_RICHTEXT</RichLocale>
      <Column>
        <div className={styles['signing-link-container']}>
          <Heading3>GENERATE_DOR_LINK_FORM_HEADER</Heading3>
          <ColumnsLayout>
            <Select
              name="language"
              label="Language"
              placeholder="Select a language"
              options={options}
              isRequired
              value={language}
              onChange={setLanguage}
              noErrorTranslation
            />
            <Button
              disabled={!language || mutation.isLoading}
              name="generate-dor-link-button"
              onClick={onClick}
              size="medium"
            >
              GENERATE_DOR_LINK_BUTTON_TEXT
            </Button>
          </ColumnsLayout>
          <Text className="ink">
            <Locale>SEND_DOR_LINK_TO_CUSTOMER</Locale>
          </Text>
          <div className={styles.signingLink}>
            <LinkPreview
              showCopyButton={mutation.isSuccess}
              isLoading={mutation.isLoading}
              hasError={mutation.isError}
              errorText="LINK_PREVIEW_DOR_ERROR"
              loadingText="LINK_PREVIEW_DOR_LOADING"
              placeholder="LINK_PREVIEW_DOR_PLACEHOLDER"
            >
              {mutation.data?.signingLink}
            </LinkPreview>
          </div>
        </div>
      </Column>
    </Container>
  );
}

export default React.memo(DORSigningLinkStep);
